import { useEffect, useState } from "react";
import client from "@/lib/api";
import { useNavigate } from "@/lib/useNav";
import { MultiSelect } from "@/components/form/MultiSelect";
import { CampaignStatusPill } from "@/components/modals/CampaignModal";
import { Briefcase, Megaphone } from "lucide-react";

const fmt = (d) => (d ? new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "");
const label = (c) => `${c.name}${c.agent_name ? ` · ${c.agent_name}` : ""}`;

export function useAgentCampaignOptions() {
  const [opts, setOpts] = useState([]);
  useEffect(() => { client.get("/campaigns", { params: { kind: "agent" } }).then(({ data }) => setOpts(data.map((c) => ({ value: c.id, label: label(c) })))); }, []);
  return opts;
}

export function OppCampaignLinks({ opp, onSave }) {
  const navigate = useNavigate();
  const opts = useAgentCampaignOptions();
  return (
    <div className="space-y-1.5 px-1" data-testid="opp-campaign-links">
      <MultiSelect value={opp.campaign_ids || []} onChange={(v) => onSave({ campaign_ids: v })} options={opts} testid="opp-inline-campaigns" placeholder="Link agent campaigns…" />
      {(opp.campaigns || []).map((c) => (
        <button key={c.id} type="button" onClick={() => navigate(`/campaigns/agent?open=${c.id}`)} data-testid={`opp-campaign-link-${c.id}`}
          className="flex w-full items-center gap-2 rounded-lg border border-slate-100 px-2 py-1.5 text-left hover:border-sky-200 hover:bg-sky-50/60">
          <Megaphone className="h-3.5 w-3.5 shrink-0 text-sky-600" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-slate-800">{c.name}</span>
            <span className="block truncate text-[11px] text-slate-400">{[c.agent_name, c.on_sale_start && `${fmt(c.on_sale_start)} → ${fmt(c.on_sale_end || c.on_sale_start)}`].filter(Boolean).join(" · ")}</span>
          </span>
          <CampaignStatusPill status={c.status} />
        </button>
      ))}
    </div>
  );
}

export function CampaignOppLinks({ campaignId }) {
  const navigate = useNavigate();
  const [opps, setOpps] = useState(null);
  useEffect(() => { client.get("/opportunities", { params: { campaign_id: campaignId } }).then(({ data }) => setOpps(data)).catch(() => setOpps([])); }, [campaignId]);
  if (!opps?.length) return null;
  return (
    <div data-testid="cmp-linked-opps">
      <label className="mb-1.5 block text-xs font-semibold text-slate-600">Linked Opportunities</label>
      <div className="space-y-1.5">
        {opps.map((o) => (
          <button key={o.id} type="button" onClick={() => navigate(`/opportunities/${o.id}`)} data-testid={`cmp-linked-opp-${o.id}`}
            className="flex w-full items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-left hover:border-sky-200 hover:bg-sky-50/60">
            <Briefcase className="h-3.5 w-3.5 shrink-0 text-sky-600" />
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{o.title}</span>
            {o.stage_name && <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">{o.stage_name}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}
